import { supabase } from '../lib/supabase'
import {
  BaseConhecimentoArticle,
  BaseConhecimentoVersion,
  BaseConhecimentoStats,
  CreateBaseConhecimentoPayload,
  UpdateBaseConhecimentoPayload,
  BaseConhecimentoFilters
} from '../types/baseConhecimento'

function generateSlug(text: string): string {
  if (!text || text.trim() === '') {
    return `base-conhecimento-${Date.now()}`
  }

  const slug = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()

  if (!slug || slug === '') {
    return `base-conhecimento-${Date.now()}`
  }

  return slug
}

async function generateUniqueArticleSlug(title: string, excludeId?: string, forceUnique: boolean = false): Promise<string> {
  const baseSlug = generateSlug(title)

  if (forceUnique) {
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    return `${baseSlug}-${uniqueSuffix}`
  }

  if (/^base-conhecimento-\d+$/.test(baseSlug)) {
    return baseSlug
  }

  try {
    const { data: existing, error } = await supabase
      .from('ai_knowledge_base_articles')
      .select('id, slug')
      .ilike('slug', `${baseSlug}%`)

    if (error) {
      console.warn('Erro ao verificar slugs existentes:', error)
      const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      return `${baseSlug}-${uniqueSuffix}`
    }

    const existingSlugs = new Set(
      (existing || [])
        .filter((row: { id: string; slug: string }) => !excludeId || row.id !== excludeId)
        .map((row: { id: string; slug: string }) => row.slug)
    )

    if (!existingSlugs.has(baseSlug)) {
      return baseSlug
    }

    let suffix = 2
    while (existingSlugs.has(`${baseSlug}-${suffix}`)) {
      suffix += 1
      if (suffix > 10000) {
        const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        return `${baseSlug}-${uniqueSuffix}`
      }
    }
    return `${baseSlug}-${suffix}`
  } catch (err) {
    console.error('Erro ao gerar slug único:', err)
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    return `${baseSlug}-${uniqueSuffix}`
  }
}

// ===== USUÁRIOS =====
export async function getUserInfo(userId: string): Promise<{ name: string; email: string } | null> {
  try {
    const { data, error } = await supabase.rpc('get_user_info', {
      user_id: userId
    })

    if (error || !data) {
      return null
    }

    return {
      name: data.name || 'Usuário',
      email: data.email || ''
    }
  } catch (error) {
    console.error('Erro ao buscar informações do usuário:', error)
    return null
  }
}

export async function getMultipleUsersInfo(userIds: string[]): Promise<Record<string, { name: string; email: string }>> {
  try {
    const { data, error } = await supabase.rpc('get_multiple_users_info', {
      user_ids: userIds
    })

    if (error) {
      console.error('Erro na função RPC:', error)
      return {}
    }

    if (!data) {
      return {}
    }

    const userMap: Record<string, { name: string; email: string }> = {}
    if (Array.isArray(data)) {
      data.forEach((user: { id: string; name?: string; email?: string }) => {
        userMap[user.id] = {
          name: user.name || 'Usuário',
          email: user.email || ''
        }
      })
    }

    return userMap
  } catch (error) {
    console.error('Erro ao buscar informações dos usuários:', error)
    return {}
  }
}

export async function getAllUsers(): Promise<Array<{ id: string; name: string }>> {
  try {
    const { data: allProfilesData, error: allProfilesError } = await supabase
      .from('profiles')
      .select('id, name, is_active')
      .order('name', { ascending: true })

    if (allProfilesError) {
      throw new Error(`Erro na consulta: ${allProfilesError.message}`)
    }

    if (!allProfilesData || allProfilesData.length === 0) {
      return []
    }

    const activeUsers = allProfilesData.filter(user => user.is_active === true)
    const usersToReturn = activeUsers.length > 0 ? activeUsers : allProfilesData

    return usersToReturn.map((user: { id: string; name?: string }) => ({
      id: user.id,
      name: user.name || 'Usuário'
    }))
  } catch {
    return []
  }
}

// ===== ARTIGOS =====
export async function updateArticlesWithoutUser(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      return
    }

    const { error } = await supabase
      .from('ai_knowledge_base_articles')
      .update({ created_by: user.id })
      .is('created_by', null)

    if (error) {
      console.error('Erro ao atualizar artigos:', error)
    }
  } catch (error) {
    console.error('Erro ao atualizar artigos:', error)
  }
}

// ===== VERSIONAMENTO =====
export async function createArticleVersion(
  articleId: string,
  versionNumber: number,
  title: string,
  body: string,
  changeSummary?: string
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    const { error } = await supabase
      .from('ai_knowledge_base_article_versions')
      .insert({
        article_id: articleId,
        version_number: versionNumber,
        title,
        body,
        created_by: user.id,
        change_summary: changeSummary
      })

    if (error) {
      console.error('Erro ao inserir versão:', error)
      throw new Error(`Erro ao criar versão: ${error.message}`)
    }
  } catch (error) {
    console.error('Erro ao criar versão do artigo:', error)
    throw error
  }
}

export async function getArticleVersions(articleId: string): Promise<BaseConhecimentoVersion[]> {
  try {
    const { data, error } = await supabase
      .from('ai_knowledge_base_article_versions')
      .select('*')
      .eq('article_id', articleId)
      .order('version_number', { ascending: false })

    if (error) {
      throw new Error(`Erro ao buscar versões: ${error.message}`)
    }

    const versions = data || []

    const authorIds = Array.from(new Set(
      versions.map((v: BaseConhecimentoVersion) => v.created_by).filter(Boolean)
    )) as string[]

    let userMap: Record<string, { name: string; email: string }> = {}
    if (authorIds.length > 0) {
      try {
        userMap = await getMultipleUsersInfo(authorIds)
      } catch (e) {
        console.warn('Falha ao resolver nomes de autores das versões:', e)
      }
    }

    return versions.map((v: BaseConhecimentoVersion) => ({
      ...v,
      user_name: v.created_by ? (userMap[v.created_by]?.name || 'Usuário') : 'Usuário'
    }))
  } catch (error) {
    console.error('Erro ao buscar versões do artigo:', error)
    return []
  }
}

export async function restoreArticleVersion(articleId: string, versionNumber: number): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    const { data: version, error: versionError } = await supabase
      .from('ai_knowledge_base_article_versions')
      .select('*')
      .eq('article_id', articleId)
      .eq('version_number', versionNumber)
      .single()

    if (versionError || !version) {
      throw new Error('Versão não encontrada')
    }

    const { error: updateError } = await supabase
      .from('ai_knowledge_base_articles')
      .update({
        title: version.title,
        body: version.body,
        updated_at: new Date().toISOString()
      })
      .eq('id', articleId)

    if (updateError) {
      throw new Error(`Erro ao restaurar versão: ${updateError.message}`)
    }

    const { data: currentArticle } = await supabase
      .from('ai_knowledge_base_articles')
      .select('current_version')
      .eq('id', articleId)
      .single()

    const newVersionNumber = (currentArticle?.current_version || 0) + 1

    await createArticleVersion(
      articleId,
      newVersionNumber,
      version.title,
      version.body,
      `Restaurado da versão ${versionNumber}`
    )

    await supabase
      .from('ai_knowledge_base_articles')
      .update({ current_version: newVersionNumber })
      .eq('id', articleId)
  } catch (error) {
    console.error('Erro ao restaurar versão:', error)
    throw error
  }
}

// ===== ESTATÍSTICAS =====
export async function getBaseConhecimentoStats(): Promise<BaseConhecimentoStats> {
  const empty: BaseConhecimentoStats = {
    total_articles: 0
  }

  try {
    const { count: totalArticles } = await supabase
      .from('ai_knowledge_base_articles')
      .select('*', { count: 'exact', head: true })

    return {
      total_articles: totalArticles || 0
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error)
    return empty
  }
}

export async function listArticles(
  filters: BaseConhecimentoFilters = {},
  page: number = 1,
  limit: number = 10
): Promise<{ articles: BaseConhecimentoArticle[]; total: number; page: number; limit: number; totalPages: number }> {
  let query = supabase
    .from('ai_knowledge_base_articles')
    .select('*')
    .order(filters.sort_by || 'created_at', { ascending: filters.sort_order === 'asc' })

  if (filters.query) {
    query = query.or(`title.ilike.%${filters.query}%,body.ilike.%${filters.query}%`)
  }

  if (filters.created_by) {
    query = query.eq('created_by', filters.created_by)
  }

  if (filters.responsible_id) {
    query = query.eq('responsible_id', filters.responsible_id)
  }

  if (filters.date_from) {
    query = query.gte('created_at', filters.date_from)
  }

  if (filters.date_to) {
    query = query.lte('created_at', filters.date_to)
  }

  const offset = (page - 1) * limit

  let countQuery = supabase
    .from('ai_knowledge_base_articles')
    .select('*', { count: 'exact', head: true })

  if (filters.query) {
    countQuery = countQuery.or(`title.ilike.%${filters.query}%,body.ilike.%${filters.query}%`)
  }
  if (filters.created_by) {
    countQuery = countQuery.eq('created_by', filters.created_by)
  }
  if (filters.responsible_id) {
    countQuery = countQuery.eq('responsible_id', filters.responsible_id)
  }
  if (filters.date_from) {
    countQuery = countQuery.gte('created_at', filters.date_from)
  }
  if (filters.date_to) {
    countQuery = countQuery.lte('created_at', filters.date_to)
  }

  const { count: totalCount } = await countQuery

  const { data, error } = await query.range(offset, offset + limit - 1)

  if (error) {
    throw new Error(`Erro ao carregar artigos: ${error.message}`)
  }

  const articles = (data || []) as BaseConhecimentoArticle[]
  const total = totalCount || 0
  const totalPages = Math.ceil(total / limit)

  return {
    articles,
    total,
    page,
    limit,
    totalPages
  }
}

export async function getArticle(id: string): Promise<BaseConhecimentoArticle | null> {
  const { data, error } = await supabase
    .from('ai_knowledge_base_articles')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null
    }
    throw new Error(`Erro ao carregar artigo: ${error.message}`)
  }

  return data as BaseConhecimentoArticle
}

export async function createArticle(payload: CreateBaseConhecimentoPayload): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser()

  let attempts = 0
  const maxAttempts = 5
  let article: { id: string } | null = null
  let articleError: { message?: string; code?: string; details?: string } | null = null

  while (attempts < maxAttempts) {
    const forceUnique = attempts > 0
    const slug = await generateUniqueArticleSlug(payload.title, undefined, forceUnique)

    const { data, error } = await supabase
      .from('ai_knowledge_base_articles')
      .insert({
        title: payload.title,
        slug,
        body: payload.body,
        responsible_id: payload.responsible_id,
        created_by: user?.id
      })
      .select('id')
      .single()

    if (error) {
      const errorMessage = (error.message || '').toLowerCase()
      const errorCode = error.code || ''
      const errorDetails = (error.details || '').toLowerCase()

      const isDuplicateSlug = (
        errorCode === '23505' ||
        errorMessage.includes('duplicate key') ||
        errorMessage.includes('unique constraint') ||
        errorMessage.includes('slug') ||
        errorDetails.includes('slug') ||
        errorMessage.includes('violates unique constraint')
      )

      if (isDuplicateSlug && attempts < maxAttempts - 1) {
        attempts++
        await new Promise(resolve => setTimeout(resolve, 200 * attempts))
        continue
      }

      if (!isDuplicateSlug && attempts < maxAttempts - 1 && errorMessage.includes('constraint')) {
        attempts++
        await new Promise(resolve => setTimeout(resolve, 200 * attempts))
        continue
      }

      articleError = error
      break
    }

    article = data
    break
  }

  if (articleError || !article) {
    throw new Error(`Erro ao criar artigo: ${articleError?.message || 'Erro desconhecido'}`)
  }

  await createArticleVersion(
    article.id,
    1,
    payload.title,
    payload.body,
    'Versão inicial'
  )

  await supabase
    .from('ai_knowledge_base_articles')
    .update({ current_version: 1 })
    .eq('id', article.id)

  return article.id
}

export async function updateArticle(payload: UpdateBaseConhecimentoPayload): Promise<void> {
  const slug = await generateUniqueArticleSlug(payload.title, payload.id)

  const { error: articleError } = await supabase
    .from('ai_knowledge_base_articles')
    .update({
      title: payload.title,
      slug,
      body: payload.body,
      responsible_id: payload.responsible_id
    })
    .eq('id', payload.id)

  if (articleError) {
    throw new Error(`Erro ao atualizar artigo: ${articleError.message}`)
  }

  const { data: lastVersion } = await supabase
    .from('ai_knowledge_base_article_versions')
    .select('version_number')
    .eq('article_id', payload.id)
    .order('version_number', { ascending: false })
    .limit(1)
    .single()

  const currentVersion = lastVersion?.version_number || 0
  const newVersionNumber = currentVersion + 1

  await createArticleVersion(
    payload.id,
    newVersionNumber,
    payload.title,
    payload.body,
    'Atualização do artigo'
  )

  await supabase
    .from('ai_knowledge_base_articles')
    .update({ current_version: newVersionNumber })
    .eq('id', payload.id)
}

export async function deleteArticle(id: string): Promise<void> {
  const { error: versionsError } = await supabase
    .from('ai_knowledge_base_article_versions')
    .delete()
    .eq('article_id', id)

  if (versionsError) {
    throw new Error(`Erro ao excluir versões do artigo: ${versionsError.message}`)
  }

  const { error } = await supabase
    .from('ai_knowledge_base_articles')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir artigo: ${error.message}`)
  }
}

export async function searchArticles(query: string): Promise<BaseConhecimentoArticle[]> {
  const { data, error } = await supabase
    .from('ai_knowledge_base_articles')
    .select('*')
    .or(`title.ilike.%${query}%,body.ilike.%${query}%`)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Erro na busca: ${error.message}`)
  }

  return (data || []) as BaseConhecimentoArticle[]
}
